/**
 * The project's architecture on the console (Work → Map): the workbench's picture — the project's layers as bands,
 * each repository's areas in the layer that claims them, the connections between repositories drawn as labelled
 * lines — from the project's document (`GET /projects/v1/:id/architecture`, here through the map) and what each
 * repository last mirrored of its own map. Read only: the picture is drawn on a member's workbench. A press on a
 * card, a line, a repository or a layer lights what belongs to it and says what it is beside the picture.
 */
import type { ProjectArchitectureResponse, ProjectRootMap } from '@aioe/shared';
import { useMemo, useState } from 'react';

import { timeAgo } from '../../hooks.ts';
import { plainText } from '../Markdown.tsx';
import { ListBlock, ListGroup } from '../ui.tsx';
import { ArchDetails, type ArchSelection } from './ProjectArchPanel.tsx';
import { ProjectArchPicture } from './ProjectArchPicture.tsx';
import { buildProjectArchModel } from './projectArchModel.ts';

const noop = () => undefined;

export function ProjectArchitectureView({ doc, roots, repositories, name }: { doc: ProjectArchitectureResponse; roots: ProjectRootMap[]; repositories: Array<{ rootId: string; name?: string }>; name?: string }) {
  const [selection, setSelection] = useState<ArchSelection>(null);
  const model = useMemo(
    () =>
      buildProjectArchModel({
        ...(name ? { name } : {}),
        architecture: doc,
        map: { roots },
        repositories: repositories.map((r) => {
          const mirrored = roots.find((x) => x.rootId === r.rootId);
          return { rootId: r.rootId, name: r.name || mirrored?.name || r.rootId, ...(mirrored ? { mirroredAt: mirrored.updatedAt } : {}) };
        }),
      }),
    [doc, roots, repositories, name],
  );
  const hint = `${model.counts.layers} ${model.counts.layers === 1 ? 'layer' : 'layers'} across ${model.counts.repositories} ${model.counts.repositories === 1 ? 'repository' : 'repositories'}, ${model.counts.connections} ${model.counts.connections === 1 ? 'connection' : 'connections'} between them${doc.updatedAt ? ` · changed ${timeAgo(doc.updatedAt)}` : ''}`;
  return (
    <ListGroup title="Project architecture" count={model.counts.layers || undefined} description={hint}>
      {model.summary ? <ListBlock className="text-sm text-muted">{plainText(model.summary)}</ListBlock> : null}
      <ListBlock flush>
        <div className="grid gap-3 p-3 @[64rem]:grid-cols-[minmax(0,1fr)_18rem]" data-project-architecture>
          <div className="min-w-0">
            <ProjectArchPicture model={model} editing={false} selection={selection} onSelect={setSelection} onMoveArea={noop} onMoveGroup={noop} onAddLayer={noop} />
          </div>
          <aside className="min-w-0 rounded-xl border border-separator p-3" aria-label="About the selection">
            <ArchDetails model={model} selection={selection} onSelect={setSelection} />
          </aside>
        </div>
      </ListBlock>
    </ListGroup>
  );
}
